/**
 * Linearly interpolate between two values.
 *
 * @param {number} start - The value returned when `amount` is 0.
 * @param {number} end - The value returned when `amount` is 1.
 * @param {number} amount - The interpolation factor, typically in the 0..1 range.
 * @returns {number} The interpolated value.
 */
export function lerp(start: number, end: number, amount: number): number {
  return start + (end - start) * amount
}

/**
 * Clamp a value into an inclusive range.
 *
 * @param {number} value - The value to clamp.
 * @param {number} minimum - The lower bound.
 * @param {number} maximum - The upper bound.
 * @returns {number} The clamped value.
 */
export function clamp(value: number, minimum: number, maximum: number): number {
  if (value < minimum) {
    return minimum
  }
  if (value > maximum) {
    return maximum
  }
  return value
}

/**
 * Re-map a value from one numeric range to another.
 *
 * @param {number} value - The source value.
 * @param {number} inputMinimum - The start of the source range.
 * @param {number} inputMaximum - The end of the source range.
 * @param {number} outputMinimum - The start of the target range.
 * @param {number} outputMaximum - The end of the target range.
 * @returns {number} The re-mapped value.
 */
export function mapRange(
  value: number,
  inputMinimum: number,
  inputMaximum: number,
  outputMinimum: number,
  outputMaximum: number,
): number {
  const span = inputMaximum - inputMinimum
  if (span === 0) {
    return outputMinimum
  }
  const normalized = (value - inputMinimum) / span
  return outputMinimum + normalized * (outputMaximum - outputMinimum)
}

/**
 * Frame-rate independent exponential smoothing toward a target value.
 *
 * @param {number} current - The current value.
 * @param {number} target - The value to approach.
 * @param {number} smoothing - The base smoothing factor per 60fps frame (0..1).
 * @param {number} deltaSeconds - The elapsed time since the previous frame.
 * @returns {number} The eased value.
 */
export function damp(current: number, target: number, smoothing: number, deltaSeconds: number): number {
  const factor = 1 - Math.pow(1 - smoothing, deltaSeconds * 60)
  return lerp(current, target, factor)
}
